import { MouseEvent, useCallback, useEffect, useState } from 'react';
import classnames from 'classnames';

import { useNavigation } from '../../hooks/navigation.hook';
import { useAuthStore, useOperatorStore } from '../../store';
import Table from '../../components/table/table.component';
import { Operator, RolePermission } from '../../types/operator.type';
import TrashIcon from '../../icons/trash.icon';
import Modal from '../../components/modal/modal';
import Button from '../../components/button/button';
import View from '../../components/view/view';
import { useSettingsStore } from '../../store/settings-store';

import styles from './operators.module.scss';

const OperatorsView = () => {
    const { navigate } = useNavigation();

    const { accessToken, permissions } = useAuthStore();
    const { theme } = useSettingsStore();
    const { employees, fetchAllEmployees, deleteOperator } = useOperatorStore();

    const [rows, setRows] = useState<any[]>([]);
    const [deletionModal, setDeletionModal] = useState({
        shown: false,
        confirmButton: {
            text: 'Sí, eliminar operador',
            disabled: false,
        },
    });
    const [deletedOperator, setDeletedOperator] = useState<
        Operator | undefined
    >();

    useEffect(() => {
        accessToken && listEmployees(accessToken, permissions);
    }, [accessToken, permissions]);

    useEffect(() => {
        if (employees.length === 0) return;
        mapOperators(employees);
    }, [employees]);

    const listEmployees = useCallback(
        (token: string, permissions: RolePermission[]) => {
            const readPermission = permissions?.find(
                (per) => per.name === 'read-operators'
            );
            if (!readPermission) return;
            fetchAllEmployees(token);
        },
        []
    );

    const onDelete = (ev: MouseEvent, id: number) => {
        ev.stopPropagation();
        setDeletedOperator(employees.find((employee) => employee.id === id));
        toggleDeletionModal();
    };

    const canDelete = (permissions?: RolePermission[]): boolean => {
        if (!permissions || !permissions.length) return false;
        return Boolean(
            permissions.find((per) => per.name === 'delete-operators')
        );
    };

    const mapOperators = useCallback(
        (operators: Operator[]) => {
            const rows = operators.map((operator) => ({
                id: operator.id,
                data: [
                    { type: 'number', value: operator.id },
                    {
                        type: 'string',
                        value: `${operator.firstName} ${operator.lastName}`,
                    },
                    {
                        type: 'string',
                        value: operator.code ?? '-',
                    },
                    {
                        type: 'string',
                        value: operator.email,
                    },
                    {
                        type: 'string',
                        value: operator.role?.displayName ?? '-',
                    },
                    {
                        type: 'component',
                        value: (
                            <button
                                className={classnames({
                                    [styles.deleteButton]:
                                        canDelete(permissions),
                                })}
                                onClick={(ev) => onDelete(ev, operator.id)}
                            >
                                <TrashIcon
                                    size={20}
                                    color={
                                        canDelete(permissions)
                                            ? '#5c636b'
                                            : '#999'
                                    }
                                />
                            </button>
                        ),
                    },
                ],
            }));
            setRows(rows);
        },
        [permissions]
    );

    const toggleDeletionModal = useCallback(() => {
        if (!canDelete(permissions)) return;
        setDeletionModal((prev) => ({
            ...prev,
            shown: !prev.shown,
        }));
    }, [permissions]);

    const confirmDeletion = async () => {
        setDeletionModal((prev) => ({
            ...prev,
            confirmButton: { text: 'Cargando...', disabled: true },
        }));
        await deleteOperator(deletedOperator!.id, accessToken!);
        setDeletionModal((prev) => ({
            ...prev,
            shown: false,
            confirmButton: { text: 'Sí, eliminar operador', disabled: false },
        }));
    };

    const goToCreation = useCallback(
        () => navigate('/create-operator'),
        [navigate]
    );

    return (
        <View
            theme={theme}
            requiredPermission="read-operators"
            permissions={permissions}
        >
            <div className={'viewContent'}>
                <h1 className={'view-title'}>Lista de operadores</h1>

                <div className={'flex align-center'} style={{ gap: 8 }}>
                    <Button onPress={goToCreation}>Crear nuevo operador</Button>
                </div>

                <Table
                    header={['ID', 'Nombre', 'Código', 'Correo', 'Rol']}
                    data={rows}
                    currentPage={1}
                    totalPages={1}
                    onPageChange={() => null}
                    onItemSelected={(id) => navigate(`/operators/${id}`)}
                />
            </div>
            <Modal shown={deletionModal.shown} onDiscard={toggleDeletionModal}>
                <div className={styles.modal}>
                    <h4>Eliminar operador</h4>
                    <span>
                        ¿Estás seguro que quieres eliminar al operador{' '}
                        <b>
                            {deletedOperator?.firstName}{' '}
                            {deletedOperator?.lastName}
                        </b>
                        ?
                    </span>
                    <div className={styles.actions}>
                        <button
                            className={styles.discard}
                            onClick={toggleDeletionModal}
                        >
                            Cerrar este modal
                        </button>
                        <button
                            className={styles.confirm}
                            disabled={deletionModal.confirmButton.disabled}
                            onClick={confirmDeletion}
                        >
                            {deletionModal.confirmButton.text}
                        </button>
                    </div>
                </div>
            </Modal>
        </View>
    );
};

export default OperatorsView;
